import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

//  Fetch all users
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch("/api/v1/users");
      if (!res.ok) throw new Error("Failed to fetch users");
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

//  Create or update user
export const saveUser = createAsyncThunk(
  "users/saveUser",
  async ({ id, data,  tempId }, { rejectWithValue }) => {
    try {
      const method = id ? "PATCH" : "POST";
      const url = id ? `/api/v1/users/${id}` : `/api/v1/users`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: data }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.errors || "Failed to save user");
      }

      //  Return updated list after save
      const savedUser = await res.json();
      // console.log("Backend returned:", savedUser);
      return  { savedUser, tempId }; // return the single created/updated user

    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Toggle active/inactive
export const toggleUserActive = createAsyncThunk(
  "users/toggleUserActive",
  async (user, { rejectWithValue }) => {
    try {
      const res = await fetch(`/api/v1/users/${user.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) throw new Error("Failed to toggle user");

      const updated = await res.json();
      return updated.user;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

//  Reset password
export const resetUserPassword = createAsyncThunk(
  "users/resetUserPassword",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await fetch(`/api/v1/users/${id}/reset_password`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: data }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.errors || "Failed to reset password");
      }
      return id; // no need to refetch whole list
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const userSlice = createSlice({
  name: "users",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {
     updateUserLastLogin: (state, action) => {
        const updated = action.payload;

        const idx = state.list.findIndex(u => u.id === updated.id);

        // User not found → do nothing (prevents crash)
        if (idx === -1) return;

        // Update only values provided by broadcast
        if (updated.last_sign_in_at) {
          state.list[idx].last_sign_in_at = updated.last_sign_in_at;
        }

        if (updated.last_sign_in_ip) {
          state.list[idx].last_sign_in_ip = updated.last_sign_in_ip;
        }
      },

   addUserOptimistic: (state, action) => {
      state.list.push(action.payload);
    },
    replaceUser: (state, action) => {
      const { tempId, user } = action.payload;
      const idx = state.list.findIndex(u => u.id === tempId);
      if (idx !== -1) state.list[idx] = user;
    },
    removeUser: (state, action) => {
      state.list = (state.list || []).filter(u => u.id !== action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      
      .addCase(saveUser.fulfilled, (state, action) => {
        const { savedUser, tempId } = action.payload;

        // If tempId exists → replace optimistic user
        if (tempId) {
          const idx = state.list.findIndex(u => u.id === tempId);
          if (idx !== -1) {
            state.list[idx] = savedUser;
            return;
          }
        }

        // If user exists → update
        const existingIdx = state.list.findIndex(u => u.id === savedUser.id);
        if (existingIdx !== -1) {
          state.list[existingIdx] = savedUser;
        } else {
          // Otherwise → add new
          state.list.push(savedUser);
        }
      })


      .addCase(toggleUserActive.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.list.findIndex((u) => u.id === updated.id);
        if (idx !== -1) state.list[idx] = updated;
      })

      
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { updateUserLastLogin, addUserOptimistic, replaceUser, removeUser } = userSlice.actions;

export default userSlice.reducer;
